import React, { useMemo } from "react"
import PropTypes from "prop-types"
import { Row, Col, Flex, Switch, Typography, Tooltip } from "antd"
import { InfoCircleOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import LabeledField from "pagesV2/client/Profile/Tabs/ProfileInfo/LabeledField"
import MultiSelect from "components/common/MultiSelect"
import MultiSelectWithSearch from "components/common/MultiSelectWithSearch"
import FilterChips from "ui/FilterChips"
import { ResourceCard } from "../ResourceCard"
import { EquipmentTable } from "../EquipmentTable"
import styles from "../../index.module.css"

const { Text, Title } = Typography

export const EquipmentSection = ({
  equipmentData,
  selectedEquipment,
  onEquipmentChange,
  selectedCategories,
  onCategoryChange,
  categoryOptions,
  selectedEquipmentData,
  onEdit,
  onRemove,
  onAddClick,
  autoAssignEquipment,
  onAutoAssignChange
}) => {
  const { t } = useTranslation()

  // Transform equipment data to MultiSelect options format
  const equipmentOptions = [
    { value: "all", label: "Select All" },
    ...(equipmentData || []).map((equipment) => ({
      value: String(equipment.id),
      label: equipment.name || equipment.equipment_title
    }))
  ]

  // Filter equipment data based on selected categories
  const filteredEquipmentData = useMemo(() => {
    // If no categories selected, show all selected equipment
    if (!selectedCategories || selectedCategories.length === 0 || selectedCategories.includes("all")) {
      return selectedEquipmentData
    }

    // Filter by category ID
    return selectedEquipmentData.filter((equipment) => {
      const categoryId = String(equipment.category?.id)
      return selectedCategories.includes(categoryId)
    })
  }, [selectedEquipmentData, selectedCategories])

  return (
    <ResourceCard title={t("listings.tabs.resources.select_equipment")} buttonText={t("listings.tabs.resources.add_new_equipment")} onAddClick={onAddClick}>
      <Flex align="center" className="mt-m" gap={8}>
        <Switch checked={autoAssignEquipment} onChange={onAutoAssignChange} />
        <Text>{t("listings.tabs.resources.booking_requires_equipment")}</Text>
        <Tooltip
          title={
            <div>
              <p>{t("listings.tabs.resources.booking_requires_equipment_tooltip_on_description")}</p>
              <p>{t("listings.tabs.resources.booking_requires_equipment_tooltip_off_description")}</p>
            </div>
          }
        >
          <InfoCircleOutlined className={styles.infoIcon} />
        </Tooltip>
      </Flex>

      <Row gutter={[16, 16]} className="mt-l">
        <Col lg={14}>
          <LabeledField labelKey="Equipment*" className="mb-0">
            <MultiSelectWithSearch
              value={selectedEquipment || []}
              onChange={onEquipmentChange}
              options={equipmentOptions}
              placeholder="Select Equipment"
              searchPlaceholder="Search equipment"
              style={{ minWidth: "100%", maxWidth: "100%" }}
              t={t}
            />
            {(selectedEquipment || []).length === 0 && (
              <Text size="caption" className={styles.emptyExperiencesText}>
                {t("equipment.no_experience_assigned", { defaultValue: "No equipment assigned yet." })}
              </Text>
            )}
          </LabeledField>
        </Col>
        <Col lg={10} className="mt-xl">
          <MultiSelect
            value={selectedCategories || []}
            onChange={onCategoryChange}
            options={categoryOptions || []}
            placeholder={t("Category") || "Category"}
            dropdownClassName={styles.dropdownButton}
          />
        </Col>
      </Row>

      <FilterChips selectedValues={selectedCategories} options={categoryOptions} onChange={onCategoryChange} className="mt-s" />

      {(selectedEquipment || []).length > 0 && (
        <div>
          <Title level={5} className={`mt-5 ${styles.selectedExperiencesTitle}`}>
            Assigned Equipment {selectedCategories?.length > 0 && !selectedCategories.includes("all") && `(Filtered: ${filteredEquipmentData.length}/${selectedEquipmentData.length})`}
          </Title>
          <EquipmentTable data={filteredEquipmentData} onEdit={onEdit} onRemove={onRemove} />
        </div>
      )}
    </ResourceCard>
  )
}

EquipmentSection.propTypes = {
  equipmentData: PropTypes.array.isRequired,
  selectedEquipment: PropTypes.array.isRequired,
  onEquipmentChange: PropTypes.func.isRequired,
  selectedCategories: PropTypes.array.isRequired,
  onCategoryChange: PropTypes.func.isRequired,
  categoryOptions: PropTypes.array.isRequired,
  selectedEquipmentData: PropTypes.array.isRequired,
  onEdit: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onAddClick: PropTypes.func.isRequired,
  autoAssignEquipment: PropTypes.bool.isRequired,
  onAutoAssignChange: PropTypes.func.isRequired
}
